import { Assets, Sprite, Texture, type Application } from 'pixi.js';

/**
 * Procedural placeholder for a 2D character.
 *
 * THIS IS NOT THE REAL RIG. It animates a single flat illustration with a
 * breathing/idle motion so the rendering pipeline (PixiJS + ticker) can be
 * proven end-to-end before any rigged asset exists.
 *
 * Per docs/bot-game/PHASE_2_2D_RIGGED_MINI_APP_VISION.md §3.1, the real
 * implementation replaces this class with a Spine2D/Rive runtime driving a
 * character cut into layers (head, eyes, mouth, arms...) produced from the
 * existing comic model sheets. Swapping the renderer is the only change
 * required — `IntroScene` only calls `load()`/`sprite`/`destroy()`, so it
 * does not need to change when the real rig arrives.
 */
export class PlaceholderCharacter {
  readonly sprite: Sprite;
  private elapsedMs = 0;
  private readonly baseScale: number;

  private constructor(sprite: Sprite, baseScale: number) {
    this.sprite = sprite;
    this.baseScale = baseScale;
  }

  static async load(imageUrl: string): Promise<PlaceholderCharacter> {
    const texture: Texture = await Assets.load(imageUrl);
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5, 1);
    const baseScale = 1;
    sprite.scale.set(baseScale);
    return new PlaceholderCharacter(sprite, baseScale);
  }

  /** Call once per frame from the app ticker. `deltaMs` is real elapsed time. */
  update(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    const breathCycleMs = 2600;
    const phase = (this.elapsedMs % breathCycleMs) / breathCycleMs;
    const breath = Math.sin(phase * Math.PI * 2) * 0.012;
    this.sprite.scale.set(this.baseScale + breath, this.baseScale - breath * 0.5);

    const bobCycleMs = 3400;
    const bobPhase = (this.elapsedMs % bobCycleMs) / bobCycleMs;
    this.sprite.y = Math.sin(bobPhase * Math.PI * 2) * 6;
  }

  destroy(): void {
    this.sprite.destroy();
  }
}

/** Fits the sprite's width to a target width while keeping its aspect ratio. */
export function fitWidth(sprite: Sprite, targetWidth: number): void {
  const ratio = targetWidth / sprite.texture.width;
  sprite.scale.set(ratio);
}

export function centerHorizontally(sprite: Sprite, app: Application): void {
  sprite.x = app.screen.width / 2;
}
